import { environment } from '../../../../../environments/environment';
import { CoreStore } from '../../../../core/store/core.store';
import { LoginCommonMode } from '../../../../shared/components/login-common/login-common.component';
import { LoginError, LoginService } from '../../services/login.service';
import {
  getLoginDescription,
  getLoginError,
  getLoginErrorStr,
  getLoginJWTToken,
  getLoginUser,
  isGoogleSignInVisible,
  isLoginValidating,
  isMicrosoftSignInVisible
} from '../../store/login.selectors';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FirebaseEventsEnum, FirebaseService } from '@ppl/firebase';
import { I18nService } from '@ppl/i18n';
import { PplDialogService } from '@ppl/ui/dialog';
import { defaultRetryStrategy } from '@ppl/utils/polling.utils';
import { fetchScript } from '@ppl/utils/script.utils';
import { unsubscribe } from '@ppl/utils/subscription.utils';
import {
  BehaviorSubject,
  from,
  NEVER,
  throwError
} from 'rxjs';
import { catchError, map, retryWhen } from 'rxjs/operators';
import type {
  OnDestroy,
  OnInit
} from '@angular/core';
import type {
  Observable, Subscription
} from 'rxjs';
import type { LoginCommonCredentials } from '../../../../shared/components/login-common/login-common.component';
import type {
  Credentials,
  LoginAPIResponse
} from '../../services/login.service';
import type { LoginUser } from '../../store/login.reducer';


@Component({
    selector: 'ppl-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {

  validating$: Observable<boolean>;

  user$: Observable<Pick<LoginUser, 'email' | 'name'>>;
  googleSignInVisible$: Observable<boolean>;
  microsoftSignInVisible$: Observable<boolean>;
  signUpUrl: string;
  description: string;
  googleAuth2LibraryLoaded$ = new BehaviorSubject<boolean>(false);
  googleTokenClient: google.accounts.oauth2.TokenClient;

  mode = LoginCommonMode.Email;

  error$: Observable<LoginError>;
  error_str$: Observable<string>;

  subscriptions: Subscription[] = [];

  constructor(
    private loginService: LoginService,
    private i18nService: I18nService,
    private store: CoreStore,
    private changeDetectorRef: ChangeDetectorRef,
    private dialogService: PplDialogService,
    private firebaseService: FirebaseService
  ) {
    this.validating$ = this.store.select(isLoginValidating);
    this.user$ = this.store.select(getLoginUser).pipe(
      map(loginUser => {
        const { email, name } = loginUser;
        return {
          email,
          name
        };
      })
    );
    this.googleSignInVisible$ = this.store.select(isGoogleSignInVisible);
    this.microsoftSignInVisible$ = this.store.select(isMicrosoftSignInVisible);
    this.error$ = this.store.select(getLoginError);
    this.error_str$ = this.store.select(getLoginErrorStr);
    this.signUpUrl = environment.signUpUrl;
  }

  ngOnInit() {
    this.description = this.store.get(getLoginDescription) || this.i18nService.translate('Continue_to_Coevera_CRM');

    this.subscriptions.push(
      this.loadGoogleAuth2Library().subscribe(() => {
        this.googleAuth2LibraryLoaded$.next(true);
        this.googleTokenClient = google.accounts.oauth2.initTokenClient({
          client_id: environment.google.client_id,
          callback: this.onGoogleLoginCallback.bind(this),
          error_callback: this.onGoogleLoginErrorCallback.bind(this),
          scope: [
            'https://www.googleapis.com/auth/userinfo.email',
            'https://www.googleapis.com/auth/userinfo.profile',
            'openid'
          ].join(' '),
          prompt: 'consent'
        });

        this.changeDetectorRef.detectChanges();
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(unsubscribe);
  }

  setMode(mode: LoginCommonMode) {
    this.mode = mode;
  }

  login(credentials: Credentials) {
    this.firebaseService.logEvent(FirebaseEventsEnum.Login, { settings: null });

    this.loginService.login(credentials).pipe(
      catchError(error => {
        return error === LoginError.ERROR_INCORRECT_LOGIN
          ? NEVER
          : throwError(error);
      })
    ).subscribe(
      (data: LoginAPIResponse) => {
        this.setMode(LoginCommonMode.Password);
      }
    );
  }

  onSubmit(credentials: LoginCommonCredentials) {
    const { email, password, remember } = credentials;
    this.store.dispatch('Login_SetLoginUserEmail', email);
    this.login({
      username: email,
      password,
      remember
    });
  }

  onEditEmail() {
    this.store.dispatch('Login_SetError', { error: LoginError.NO_ERROR });
    this.setMode(LoginCommonMode.Email);
  }

  onMicrosoftLogin() {
    this.firebaseService.logEvent(FirebaseEventsEnum.Login, { settings: 'microsoft' });

    const jwt = this.store.get(getLoginJWTToken);
    let url = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';
    const redirectUrl = `${window.location.protocol}//${window.location.host}/oauth2/ms_login`;
    url += '?scope=user.read';
    url += '&client_id=' + environment.ms.client_id;
    url += '&response_type=code';
    url += '&response_mode=query';
    url += '&prompt=select_account';
    // NOTE(mike): Azure AD is not allowing query params in authorized redirect
    // URIs. Therefore we use the `state` query param.
    url += `&state=${jwt}`;
    url += '&redirect_uri=' + redirectUrl;

    window.location.href = url;
  }

  loadGoogleAuth2Library() {
    return from(fetchScript({ url: 'https://accounts.google.com/gsi/client' })).pipe(
      retryWhen(defaultRetryStrategy())
    );
  }

  onGoogleLoginErrorCallback(error: google.accounts.oauth2.ClientConfigError) {
    switch (error.type) {
      case 'popup_failed_to_open':
        this.dialogService.alert({
          text: this.i18nService.translate('The_google_authentication_popup_window_failed_to_open.')
        });
      case 'unknown':
        console.error(`Google login error callback: Unknown error: ${error.message}`);
        this.dialogService.alert({
          text: this.i18nService.translate('Unknown_error._Please_contact_support.')
        });
    }
  }

  onGoogleLoginCallback(response: google.accounts.oauth2.TokenResponse) {
    if (response.error) {
      console.error(`Google login callback: Error: ${response.error}, ${response.error_description}`);
    } else {
      const jwt = this.store.get(getLoginJWTToken);
      const redirectUrl = `${window.location.protocol}//${window.location.host}/oauth2/google_login`;

      let url = redirectUrl;
      url += `?access_token=${response.access_token}&jwt=${jwt}`;
      window.location.href = url;
    }
  }

  onGoogleLogin() {
    this.firebaseService.logEvent(FirebaseEventsEnum.Login, { settings: 'google' });
    this.googleTokenClient.requestAccessToken();
  }
}
